'use client';

import cl from 'clsx';
import useAuthStore from '@/store/auth';
import { useEffect, useRef, useState } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';
import Link from 'next/link';
import routes from '@/utils/routes';
import { usePathname } from 'next/navigation';
import styles from './styles.module.css';
import './styles.css';

/** @type {{ select: { tools_list: any } }} */

export default function Select({ options, defaultText, type, onClick, onDataFromChild, dict }) {
    const { tools, pa_tool, sa_tool, lca_tool, sp_tool, ast_tool } = dict?.select?.tools_list ?? {};
    const { my_account, log_out } = dict.select.account;
    const { logout } = useAuthStore();
    const pathname = usePathname();

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(defaultText);
    const [selectedId, setSelectedId] = useState(null);

    const selectRef = useRef(null);

    const getNavLink = (title) => {
        switch (title) {
            case ast_tool:
                return routes.ast_tool;
            case pa_tool:
                return routes.pa_tool;
            case sa_tool:
                return routes.sa_tool;
            case lca_tool:
                return routes.lca_tool;
            case sp_tool:
                return routes.sp_tool;
            case my_account:
                return routes.account;
            default:
                return '#';
        }
    };

    /**
     * General function of element selection.
     * closeImmediately = true — Close the menu immediately (buttons, contact-us etc.)
     * closeImmediately = false — do not touch open, the menu will be closed when changing the route.
     */
    const select = (id, title, uuid, short_name, options = {}) => {
        const { closeImmediately = true } = options;
        setSelectedId(id);

        if (closeImmediately) {
            setOpen(false);
        }

        if (onClick) {
            onClick();
        }

        const pathWithoutLocale = pathname.split('/').slice(2).join('/');

        if (type === 'account') {
            if (pathWithoutLocale === 'contacts') {
                setSelected(my_account);
            } else {
                setSelected(title || short_name);
            }
        } else {
            setSelected(title || short_name);
        }

        if (onDataFromChild) {
            onDataFromChild(title, uuid);
        }

        if (title === log_out) {
            logout();
        }
    };

    // when changing route we always close dropdown
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOpen(false);
    }, [pathname]);

    // logic for selection type 'account'
    useEffect(() => {
        const pathWithoutLocale = pathname.split('/').slice(2).join('/');

        if (type === 'account') {
            if (pathWithoutLocale === 'account') {
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setSelected(my_account);
            } else {
                setSelected(defaultText);
            }
        }
    }, [pathname, type, my_account, defaultText]);

    // Click out of selection - close
    const handleClickOutside = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
            setOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const pathWithoutLocale = pathname.split('/').slice(2).join('/');

        if (type === 'nav') {
            if (
                pathWithoutLocale === '' ||
                pathWithoutLocale === 'faq' ||
                pathWithoutLocale === 'contacts' ||
                pathWithoutLocale === 'account'
            ) {
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setSelected(tools);
            }

            if (pathWithoutLocale.startsWith('alignment-tool')) {
                setSelected(ast_tool);
            }
        }
    }, [pathname, type, tools, ast_tool]);

    return (
        <div
            className={cl(
                styles.select,
                type === 'account' && styles.select__account,
                type === 'quiz' && styles.select__account,
                type === 'title' && styles.select__title_project,
                type === 'dark-title' && styles.select__title_project,
                type === 'dark-title' && styles.select__dark
            )}
            ref={selectRef} data-name="select"
        >
            <div
                onClick={() => setOpen(!open)}
                className={cl(
                    styles.select__lable,
                    type === 'nav' && styles.select__lable_nav,
                    type === 'title' && styles.select__lable_title,
                    type === 'dark-title' && styles.select__lable_title_dark
                )}
            >
                <p
                    className={cl(
                        styles.select__title,
                        type === 'nav' && styles.select__title_nav,
                        type === 'nav' && 'custom-underline',
                        type === 'account' && styles.select__title_account,
                        selected !== 'Type of question' && styles.select__selected_text,
                        selected === 'Category' && styles.select__default_text,
                        selected === 'Product/asset' && styles.select__default_text
                    )}
                >
                    {selected || defaultText}
                </p>
                <MdKeyboardArrowDown
                    className={cl(
                        styles.select_arrow,
                        type === 'dark-title' && styles.select__arrow_white,
                        open && styles.select__arrow_top
                    )}
                />
            </div>

            {open && (
                <div
                    className={cl(
                        styles.select__selectedList,
                        type === 'nav' && styles.select__selectedList_nav,
                        type === 'quiz' && styles.select__selectedList_quiz,
                        type === 'title' && styles.select__selectedList_title
                    )}
                >
                    {options.map(({ title, uuid, short_name }, index) => {
                        const href = getNavLink(title);
                        const hasRoute = href && href !== '#';
                        const isActive = selectedId === index + 1;
                        const label = title || short_name;
                        const key = uuid ?? index;

                        if (type !== 'contact-us' && hasRoute) {
                            return (
                                <Link
                                    key={key}
                                    href={href}
                                    className="w-full text-center flex"
                                    onClick={() =>
                                        select(index + 1, title, uuid, short_name, { closeImmediately: false })
                                    }
                                >
                                    <div
                                        className={cl(styles.select__text, 'py-3 w-full transition-colors duration-200')}
                                    >
                                        {label}
                                    </div>
                                </Link>
                            );
                        }

                        return (
                            <div
                                key={key}
                                className={cl(styles.select__text, { [styles.active]: isActive })}
                            >
                                <button
                                    type="button"
                                    onClick={() =>
                                        select(index + 1, title, uuid, short_name, { closeImmediately: true })
                                    }
                                >
                                    {label}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
